import axios from 'axios'
import { useRef, useState } from 'react'
import { constants } from '../../Constants'
import { compressImage } from '../../utils/compressImage'
import { Button, Modal } from '../ui'
import { useToast } from '../ui/Toast/Toast'

const AVATAR_COLORS = [
  '#2440ab',
  '#3b82f6',
  '#2563eb',
  '#0ea5e9',
  '#06b6d4',
  '#14b8a6',
  '#10b981',
  '#22c55e',
  '#84cc16',
  '#eab308',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#ec4899',
  '#d946ef',
  '#a855f7',
  '#8b5cf6',
  '#6366f1',
  '#64748b',
  '#0f766e',
  '#166534',
  '#7c2d12',
  '#991b1b',
  '#7e22ce',
  '#1d4ed8',
]

const getInitials = (name = '', lastname = '') => {
  const first = name?.trim()?.[0] || ''
  const last = lastname?.trim()?.[0] || ''
  return (first + last).toUpperCase() || '?'
}

export const ModalChooseAvatar = ({ user, onAvatarChosen }) => {
  const [step, setStep] = useState('choose') // 'choose' | 'initials' | 'uploading'
  const [selectedColor, setSelectedColor] = useState(AVATAR_COLORS[0])
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef(null)
  const toast = useToast()

  const initials = getInitials(user?.name, user?.lastname)

  const updateLocalStorage = updates => {
    const storedUser = JSON.parse(localStorage.getItem('user'))
    localStorage.setItem('user', JSON.stringify({ ...storedUser, ...updates }))
  }

  const handleUploadPhoto = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    const validExts = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'heic',
      'heif',
      'bmp',
    ]
    const ext = file.name?.split('.').pop()?.toLowerCase()
    // file.type can be empty on Android content:// URIs — trust accept="image/*" in that case
    if (file.type && !file.type.startsWith('image/') && !validExts.includes(ext)) {
      toast({ title: 'Solo se permiten imágenes', status: 'error' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'La imagen no puede superar los 5 MB', status: 'error' })
      return
    }

    setLoading(true)
    setStep('uploading')
    try {
      const base64 = await compressImage(file)

      const response = await axios.post(`${constants.urlLocal}upload-avatar`, {
        userId: user.id || user._id,
        image: base64,
      })

      if (response.data.ok) {
        updateLocalStorage({
          avatar: response.data.avatar,
          avatarType: 'image',
          avatarColor: null,
        })
        toast({ title: 'Foto de perfil guardada', status: 'success' })
        onAvatarChosen()
      }
    } catch (error) {
      toast({
        title: 'Error al subir la foto',
        description: error.response?.data?.message || error.message,
        status: 'error',
      })
      setStep('choose')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveInitials = async () => {
    setLoading(true)
    try {
      const response = await axios.post(
        `${constants.urlLocal}save-avatar-initials`,
        {
          userId: user.id || user._id,
          color: selectedColor,
        }
      )

      if (response.data.ok) {
        updateLocalStorage({
          avatar: null,
          avatarType: 'initials',
          avatarColor: selectedColor,
        })
        toast({ title: 'Avatar guardado', status: 'success' })
        onAvatarChosen()
      }
    } catch (error) {
      toast({
        title: 'Error al guardar el avatar',
        description: error.response?.data?.message || error.message,
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      closable={false}
      title="📸 Elige tu avatar"
    >
      <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
        Personaliza tu perfil eligiendo una foto o un avatar con tus iniciales.
      </p>

      {step === 'choose' && (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
        >
          {/* Opción 1: subir foto */}
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              background: '#fafafa',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
            onMouseOver={e => (e.currentTarget.style.borderColor = '#2440ab')}
            onMouseOut={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#2440ab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontWeight: 600, margin: 0, fontSize: '0.95rem' }}>
                Subir una foto
              </p>
              <p style={{ margin: 0, color: '#888', fontSize: '0.8rem' }}>
                Sube una imagen desde tu dispositivo
              </p>
            </div>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleUploadPhoto}
          />

          {/* Opción 2: avatar con iniciales */}
          <button
            onClick={() => setStep('initials')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              background: '#fafafa',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
            onMouseOver={e => (e.currentTarget.style.borderColor = '#2440ab')}
            onMouseOut={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: selectedColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'white',
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              {initials}
            </div>
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontWeight: 600, margin: 0, fontSize: '0.95rem' }}>
                Usar mis iniciales
              </p>
              <p style={{ margin: 0, color: '#888', fontSize: '0.8rem' }}>
                Elige un color de fondo para tus iniciales
              </p>
            </div>
          </button>

          {/* saltar paso */}
          <Button
            variant="ghost"
            onClick={() => {
              updateLocalStorage({
                avatar: null,
                avatarType: 'initials',
                avatarColor: AVATAR_COLORS[0],
              })
              onAvatarChosen()
            }}
            style={{ alignSelf: 'center', marginTop: '0.5rem' }}
          >
            Omitir por ahora
          </Button>
        </div>
      )}

      {step === 'initials' && (
        <div>
          {/* Vista previa */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1.25rem',
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: selectedColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
                fontSize: '2rem',
                transition: 'background 0.2s',
              }}
            >
              {initials}
            </div>
          </div>

          {/* Selector de color */}
          <p
            style={{
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: '0.5rem',
              color: '#333',
            }}
          >
            Elige un color:
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginBottom: '1.25rem',
            }}
          >
            {AVATAR_COLORS.map(color => (
              <button
                key={color}
                onClick={() => setSelectedColor(color)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: color,
                  border:
                    selectedColor === color
                      ? '3px solid #111'
                      : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, border 0.15s',
                  transform:
                    selectedColor === color ? 'scale(1.15)' : 'scale(1)',
                }}
              />
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '0.5rem',
            }}
          >
            <Button variant="ghost" onClick={() => setStep('choose')}>
              ← Volver
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveInitials}
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar avatar'}
            </Button>
          </div>
        </div>
      )}

      {step === 'uploading' && (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: '3px solid #e5e7eb',
              borderTopColor: '#2440ab',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 1rem',
            }}
          />
          <p style={{ color: '#555' }}>Subiendo tu foto...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </Modal>
  )
}
